import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import type { Sync } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'
import { Status } from 'modules/payroll_integrations_datev_lauds/lib/IncidenceSync/store'

import Available from './Banners/Available'
import Blocked from './Banners/Blocked'
import Done from './Banners/Done'
import MissingEmployeeCode from './Banners/MissingEmployeeCode'
import PartialSync from './Banners/PartialSync'
import Pending from './Banners/Pending'
import Errored from './Errored'

interface Props {
  sync: Sync
  syncing: boolean
  onSync: () => void
  onStatusCheck: () => void
  checkingStatus: boolean
  pendingState: boolean
  setPendingState: (value: boolean) => void
  incidenceType: keyof typeof INCIDENCE_TYPES
}

const PersonalData = ({
  sync,
  syncing,
  onSync,
  onStatusCheck,
  checkingStatus,
  pendingState,
  setPendingState,
  incidenceType,
}: Props) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()

  const syncContent = () => {
    switch (sync.status) {
      case Status.Available:
        return (
          <Available
            onSync={onSync}
            syncing={syncing}
            title={i18n.t(
              'payroll_integrations.incidences.datev_lauds.personal_data.available.title'
            )}
            description={i18n.t(
              'payroll_integrations.incidences.datev_lauds.personal_data.available.message'
            )}
            submitButton={i18n.t(
              'payroll_integrations.incidences.datev_lauds.personal_data.available.submit'
            )}
          />
        )
      case Status.Pending:
        return (
          <Pending
            checkingStatus={checkingStatus}
            onStatusCheck={onStatusCheck}
            pendingState={pendingState}
            pendingOver24h={sync.pending_over_24h ?? false}
          />
        )
      case Status.Done:
        setPendingState(false)
        ;(track(trackingEvents.payrollIntegrationsDatevLauds.datevLaudsEmployeeUpdated),
          {
            company_id: companyId,
            access_id: accessId,
            type: incidenceType,
          })
        return sync?.unsync_fields && sync?.unsync_fields.length > 0 ? (
          <PartialSync unsync_fields={sync?.unsync_fields} incidenceType={incidenceType} />
        ) : (
          <Done
            title={i18n.t('payroll_integrations.incidences.datev_lauds.personal_data.done.title')}
            description={i18n.t(
              'payroll_integrations.incidences.datev_lauds.personal_data.done.message'
            )}
          />
        )

      case Status.Unavailable:
        return sync.error_code ? (
          <Errored
            error={JSON.stringify({ error_code: sync.error_code })}
            onSync={onSync}
            syncing={syncing}
          />
        ) : <MissingEmployeeCode onSync={onSync} syncing={syncing} />
      case Status.Blocked:
        setPendingState(false)
        return (
          <Blocked
            sync={sync}
            onSync={onSync}
            syncing={syncing}
            submitButton={i18n.t('payroll_integrations.incidences.datev_lauds.hiring.error.retry')}
          />
        )
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  const background = () => {
    switch (sync.status) {
      case Status.Available:
        return 'darker080'
      case Status.Unavailable:
        return 'radicalRed200'
      case Status.Done:
        return sync?.unsync_fields && sync?.unsync_fields.length > 0 ? 'tangerine200' : 'lime200'
      case Status.Pending:
        return 'viridian100'
      case Status.Blocked:
        return 'radicalRed200'
      default:
        throw new Error(`Rendering unknown status ${sync.status}`)
    }
  }

  if (sync.status === Status.Unavailable && sync.error_code) {
    return syncContent()
  }

  return (
    <Box
      background={background()}
      paddingX="s24"
      paddingY="s16"
      borderRadius={{
        all: 'abs008',
      }}
      gap="s16"
    >
      {syncContent()}
    </Box>
  )
}

export default PersonalData
